import moment from 'moment'
import CreditStatus from '../common/CreditStatus'
import ClientService from '../services/ClientService'
import CreditsManagerService from '../services/CreditsManagerService'
import InvoiceService from '../services/InvoiceService'
import IndicatorsService from '../services/IndicatorsService'

export default {
  methods: {
    async goToDetail (folio) {
      const selectedProduct = this.activePortafolio.find(p => p.folio === folio)
      const routeName = this.getRouteName(selectedProduct?.product?.code)
      const query = routeName.includes('factoring') ? {} : { folio }

      if (routeName) {
        this.$router.push({
          name: routeName,
          query
        })
      }
    },

    redirectToHome () {
      this.$router.push({ name: 'portafolio' })
    },

    getRouteName (productType) {
      switch (true) {
      case ['BI', 'FB'].includes(productType): return 'factoringDetail'
      case ['CS', 'LE', 'CR'].includes(productType): return 'amortizableDetail'
      case ['CCF', 'HA', 'PO', 'RF'].includes(productType): return 'revolvingDetail'
      default: return 'underConstruction'
      }
    },

    async setClientPortafolio (clientId) {
      // @PILOT - start
      // Antes de obtener sus productos, validar si forma parte del grupo piloto
      const { hasAccess } = await ClientService.hasPilotAccess(this.$store?.getters?.user?.clientId)
      if (!hasAccess) {
        this.$store.commit('SET_ACTIVE_PORTAFOLIO', [])
        this.$store.commit('SET_CLOSED_PORTAFOLIO', [])
        return
      }
      // @PILOT - end

      const { items } = await CreditsManagerService.getPortafolio(clientId)

      const active = items.filter(c => [CreditStatus.ETAPA_1, CreditStatus.ETAPA_2, CreditStatus.ETAPA_3].includes(c.status))
      const inactive = items.filter(c => [CreditStatus.CERRADO].includes(c.status)).map(c => {
        return {
          ...c,
          interestRate: `${c.baseInterest === 'FIXED' ? '' : c.baseInterest}${c.baseInterest === 'FIXED' ? this.formatRate(c.annualRate) : ' + ' + this.formatRate(c.additionalBaseInterest)}`
        }
      })

      this.$store.commit('SET_ACTIVE_PORTAFOLIO', active)
      this.$store.commit('SET_CLOSED_PORTAFOLIO', inactive)
    },

    async setIndicators () {
      let indicators = await IndicatorsService.getByDate()

      indicators = Object.fromEntries(
        Object.entries(indicators).map(([key, value]) => [
          key.toUpperCase(),
          value
        ])
      )

      indicators.SOURCE = 'BANXICO'

      this.$store.commit('SET_INDICATORS', indicators)
    },

    async downloadInvoice ({ id = null, fileType = 'pdf', fileName = '' }) {
      if (!id) throw new Error('No se recibió una factura para descargar')
      const loader = this.$loading.show()
      try {
        const { file } = await InvoiceService.downloadInvoiceByID({
          fileType,
          id
        })

        await this.downloadBase64({
          base64: file.Content,
          contentType: file.ContentType,
          fileName
        })
      } catch (err) {
        console.error(err)
        this.$swal({
          icon: 'error',
          title: 'Ocurrió un error',
          text: 'Hubo un problema al descargar esta factura. Por favor inténtalo nuevamente.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
      } finally {
        loader.hide()
      }
    },

    async downloadAccountStatement ({ statementType = 'credit', id, folio, selectedPeriod }) {
      const loader = this.$loading.show()
      try {
        if (!id || !selectedPeriod) {
          throw new Error('Faltan parámetros')
        }

        const serviceByType = {
          line: (params) =>
            CreditsManagerService.getCreditLineAccountStatement(params),
          credit: (params) =>
            CreditsManagerService.getCreditAccountStatement(params)
        }

        const getStatement = serviceByType[statementType]

        const { base64 } = await getStatement({
          id,
          periodoCalendarizado: selectedPeriod
        })

        await this.downloadBase64({
          base64,
          fileName: `Edo de cuenta ${folio} ${selectedPeriod}`
        })
      } catch (err) {
        console.error(err)
        this.$swal({
          icon: 'error',
          title: 'Ocurrió un error',
          text: 'Hubo un problema al descargar el Estado de Cuenta. Por favor inténtalo nuevamente.',
          confirmButtonText: 'Cerrar',
          confirmButtonColor: '#d93025'
        })
      } finally {
        loader.hide()
      }
    },

    async downloadBase64 ({ contentType = 'pdf', base64, fileName = 'file' }) {
      // Convert base64 to a downloadable file
      const link = document.createElement('a')
      link.href = `data:application/${contentType};base64,${base64}`
      link.download = `${fileName}.${contentType}`
      link.click()
    },

    formatRate (rate = 0) {
      return (rate * 100).toFixed(2)
    },

    formatCurrency (value) {
      if (isNaN(value)) return '$0.00'

      return `$${Number(value).toLocaleString('es-MX', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 3
      })}`
    },

    formatDateNumeric (date) {
      if (!date) return ''
      return new Date(date).toLocaleString('es-MX', {
        dateStyle: 'short'
      })
    },

    formatDateShortText (date, { onlyMonthYear = false } = {}) {
      if (!date) return ''

      let d

      if (typeof date === 'string' && date.includes('-')) {
        const [year, month, day] = date.split('-')
        d = new Date(year, month - 1, day)
      } else {
        d = new Date(date)
      }

      const monthText = d.toLocaleString('es-MX', { month: 'short' }).toUpperCase()
      const yearText = d.getFullYear()

      if (onlyMonthYear) {
        return `${monthText} ${yearText}`
      }

      const dayText = d.getDate().toString().padStart(2, '0')
      return `${dayText} ${monthText} ${yearText}`
    },

    getPeriodOptions (credit = {}) {
      const options = []

      // Boundaries
      const todayEnd = moment().endOf('month')

      // If the credit already has scheduled payments
      if (credit.pagosCalendarizados?.length) {
        const periods = credit.pagosCalendarizados

        for (const p of periods) {
          const periodDate = moment(p.fecha)

          // Show only months <= current month
          if (periodDate.isSameOrBefore(todayEnd, 'day')) {
            options.push({
              text: this.formatDateShortText(periodDate),
              value: periodDate.format('YYYY-MM-DD')
            })
          } else {
            // If credit is closed, show next one and break
            if (credit.status === CreditStatus.CERRADO) {
              options.push({
                text: this.formatDateShortText(periodDate),
                value: periodDate.format('YYYY-MM-DD')
              })
              break
            }
          }
        }
      } else {
        // If no scheduled payments, generate periods month by month
        const currentMonth = moment(credit.fechaInicioCredito).endOf('month')

        while (currentMonth.isSameOrBefore(todayEnd, 'day')) {
          options.push({
            text: this.formatDateShortText(currentMonth, { onlyMonthYear: true }),
            value: currentMonth.format('YYYY-MM-DD')
          })

          // Move to next month
          currentMonth.add(1, 'month').endOf('month')
        }
      }

      // Sort descending (latest date first)
      return options.sort((a, b) => moment(b.value).diff(moment(a.value)))
    },

    async mapInvoicesToPayments (paymentsHistory, startDate) {
      const { invoices } = await InvoiceService.listInvoices({
        clientId: this.$store?.getters?.user?.clientId,
        startDate: moment(startDate).format('YYYY/MM/DD'),
        endDate: moment().format('YYYY/MM/DD'),
        invoiceType: 'P',
        active: true
      })

      if (!invoices || !invoices.length) return

      // Agrega el facturamaId a los pagos
      const paymentsCopy = JSON.parse(JSON.stringify(paymentsHistory))

      paymentsCopy.forEach(p => {
        const cfdi = invoices.find(i => i.invoiceId === p?.invoice?.invoiceId)
        if (cfdi) p.invoice.facturamaId = cfdi.facturamaId
      })

      return paymentsCopy
    }
  },

  computed: {
    indicators () {
      return this.$store?.state?.indicators || {}
    },
    activePortafolio () {
      return this.$store?.state?.activePortafolio || []
    },
    closedPortafolio () {
      return this.$store?.state?.closedPortafolio || []
    },
    accessiblePortfolio () {
      // Cerrado dentro de los ultimos 6 meses
      const closed = this.closedPortafolio?.filter(c => c.endDate >= moment().subtract(6, 'months').format('YYYY-MM-DD'))

      // Activo y recientemente cerrado
      const accessible = [...this.activePortafolio, ...closed]
        .filter(c => !['CC', 'PFF', 'GC'].includes(c.product?.code)) // [(hasta que se habiliten esos productos)]

      return accessible
    },
    hasFactoring () {
      return this.accessiblePortfolio.some(c => ['BI', 'FB'].includes(c.product?.code))
    },
    hasRevolving () {
      return this.accessiblePortfolio.some(c => ['CCF', 'HA', 'PO', 'RF'].includes(c.product?.code))
    },
    hasAmortizable () {
      return this.accessiblePortfolio.some(c => ['CS', 'LE', 'CR'].includes(c.product?.code))
    }
  }
}
